import { apiRequest } from "../../../../shared/lib/api";

export type EventChallengeLink = {
  eventId: string;
  tournamentId: number;
  challengeType: string;
  challengeName?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  scheduledAt?: string | null;
  scheduledEndAt?: string | null;
  amountPerPlayerCents?: number | null;
  paymentMode?: string | null;
  registrationsOpen?: boolean;
  registrationType?: "individual" | "fixed_pair";
  shirtSizesEnabled?: boolean;
  shirtSizes?: string[];
  shirtAvailabilityType?: "none" | "period" | "quantity";
  shirtDeadlineAt?: string | null;
  shirtQuantityLimit?: number | null;
  shirtConfirmedCount?: number;
  shirtAvailableCount?: number | null;
  shirtSelectionAvailable?: boolean;
  shirtUnavailableReason?: "deadline" | "sold_out" | null;
  availabilityRestrictionsEnabled?: boolean;
  availabilityRestrictionAmountCents?: number | null;
  availabilityRestrictionLimit?: number | null;
  availabilityRestrictionDeadlineAt?: string | null;
  availabilityRestrictionTypes?: string[];
  availabilityRestrictionPeriods?: Array<{
    label: string;
    startAt: string;
    endAt: string;
    type?: string;
  }>;
  registrationCategories?: Array<{
    categoryKey: string;
    categoryName: string;
    categoryMale?: string | null;
    categoryFemale?: string | null;
    categoryGender?: "male" | "female" | "mixed" | "open" | null;
    registrationType?: "individual" | "fixed_pair" | null;
    address?: string | null;
    scheduledAt?: string | null;
    scheduledEndAt?: string | null;
    shirtSizesEnabled?: boolean;
    shirtSizes?: string[];
    slotLimit: number;
    confirmedRegistrations: number;
    remainingSlots: number;
  }>;
};

export type EventRegistrationStatus =
  | "pending"
  | "confirmed"
  | "canceled"
  | "waitlist";

export type EventRegistration = {
  id: number;
  eventId: string;
  tournamentId: number;
  status: EventRegistrationStatus;
  adminNote?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  paymentRequired: boolean;
  paymentStatus: "pending" | "confirmed" | "canceled" | "refunded";
  paymentProvider?: string | null;
  paymentReference?: string | null;
  paymentAmountCents?: number | null;
  waitlistJoinedAt?: string | null;
  waitlistConfirmedAt?: string | null;
  waitlistPosition?: number | null;
  waitlistPromotedAt?: string | null;
  waitlistPromotionReason?: string | null;
  discountCouponId?: number | null;
  discountCouponCode?: string | null;
  originalAmountCents?: number | null;
  discountAmountCents?: number | null;
  finalAmountCents?: number | null;
  paymentLink?: string | null;
  invoiceUrl?: string | null;
  paymentMethod?: string | null;
  paymentConfirmedAt?: string | null;
  paymentCanceledAt?: string | null;
  registrationType?: "individual" | "fixed_pair";
  registrationCategoryKey?: string | null;
  registrationCategoryName?: string | null;
  participantMode?: "individual" | "dupla_fixa";
  preferredSide?: "direita" | "esquerda" | "ambos" | null;
  preferredSideSource?: "registration" | "profile" | null;
  teammateCpf?: string | null;
  teammateName?: string | null;
  shirtSizes?: {
    player?: string | null;
    partner?: string | null;
  };
  availabilityRestriction?: {
    hasRestriction: boolean;
    status: "confirmed" | "pending" | "none";
    label: string;
    details: Array<{
      id: number;
      label: string;
      startAt: string;
      endAt: string;
      status: string;
      paymentStatus: string;
      amountCents: number;
    }>;
  };
  createdAt?: string;
  updatedAt?: string;
};

export type MyEventRegistrationSummary = {
  id: number;
  eventId: string;
  tournamentId: number;
  registrationCategoryKey?: string | null;
  registrationCategoryName?: string | null;
  status: EventRegistrationStatus;
  paymentStatus: "pending" | "confirmed" | "canceled" | "refunded";
  waitlistJoinedAt?: string | null;
  waitlistConfirmedAt?: string | null;
  waitlistPosition?: number | null;
  waitlistPromotedAt?: string | null;
  waitlistPromotionReason?: string | null;
};

export async function getEventChallengeLink(eventId: string) {
  return apiRequest<EventChallengeLink>(`/api/events/${encodeURIComponent(eventId)}/challenge-link`);
}

export async function createEventRegistration(
  token: string,
  eventId: string,
  payload?: {
    participantMode?: "individual" | "dupla_fixa";
    preferredSide?: "direita" | "esquerda" | "ambos";
    teammateCpf?: string;
    teammateName?: string;
    shirtSizes?: {
      player?: string;
      partner?: string;
    };
    registrationCategoryKey?: string;
    availabilityRestriction?: {
      enabled: boolean;
      periodKey?: string;
      periodKeys?: string[];
      periods?: Array<{
        periodKey?: string;
        label?: string;
        startAt?: string;
        endAt?: string;
        type?: string;
      }>;
      label?: string;
      startAt?: string;
      endAt?: string;
      type?: string;
    };
  }
) {
  return apiRequest<EventRegistration>(`/api/events/${encodeURIComponent(eventId)}/registrations`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload || {}),
  });
}

export async function updateMyEventRegistrationShirtSizes(
  token: string,
  registrationId: number,
  payload: {
    shirtSizes: {
      player?: string;
      partner?: string;
    };
  },
) {
  return apiRequest<EventRegistration>(
    `/api/me/event-registrations/${encodeURIComponent(
      String(registrationId),
    )}`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: "update_shirt_sizes",
        shirtSizes: payload.shirtSizes || {},
      }),
    },
  );
}

export async function cancelMyEventRegistration(
  token: string,
  registrationId: number,
) {
  return apiRequest<{ ok: boolean; id: number }>(
    `/api/me/event-registrations/${encodeURIComponent(
      String(registrationId),
    )}`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: "cancel_registration",
      }),
    },
  );
}

export type AvailablePartner = {
  id: number;
  name: string;
  cpf?: string | null;
  email?: string | null;
  phone?: string | null;
  sex?: string | null;
  category?: string | null;
  categoryLabel?: string | null;
  available?: boolean;
  reason?: string;
  statusLabel?: string;
};

export async function listAvailablePartners(
  token: string,
  eventId: string,
  search = "",
  registrationCategoryKey = "",
) {
  const params = new URLSearchParams();
  if (search.trim()) params.set("q", search.trim());
  if (String(registrationCategoryKey || "").trim()) {
    params.set("registrationCategoryKey", String(registrationCategoryKey).trim());
  }
  const query = params.toString();
  return apiRequest<{
    eventId: string;
    registrationType: "individual" | "fixed_pair";
    requiredCategory?: string | null;
    requiredCategoryLabel?: string | null;
    requiredGenderLabel?: string | null;
    items: AvailablePartner[];
  }>(
    `/api/events/${encodeURIComponent(eventId)}/registrations/available-partners${
      query ? `?${query}` : ""
    }`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export async function getMyEventRegistration(
  token: string,
  eventId: string,
  registrationCategoryKey = "",
) {
  const params = new URLSearchParams();
  if (String(registrationCategoryKey || "").trim()) {
    params.set(
      "registrationCategoryKey",
      String(registrationCategoryKey).trim(),
    );
  }
  const query = params.toString();
  return apiRequest<EventRegistration>(
    `/api/events/${encodeURIComponent(eventId)}/registrations/me${
      query ? `?${query}` : ""
    }`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export async function listMyEventRegistrations(token: string) {
  return apiRequest<MyEventRegistrationSummary[]>("/api/me/event-registrations", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createPaymentIntent(token: string, registrationId: number) {
  return createPaymentIntentWithPayload(token, registrationId, {});
}

export type RegistrationPayerPayload = {
  fullName: string;
  email: string;
  phone: string;
  taxId: string;
  paymentMethod: "pix" | "credit_card" | "debit_card";
  couponCode: string;
};

export type DiscountCouponPreview = {
  ok: boolean;
  couponCode?: string;
  originalAmountCents?: number;
  discountAmountCents?: number;
  baseAmountCents?: number;
  surchargeCents?: number;
  finalAmountCents?: number;
  selectedPaymentMethod?: "pix" | "credit_card" | "debit_card";
  paymentRequired?: boolean;
  error?: string;
};

export async function validateRegistrationDiscountCoupon(
  token: string,
  registrationId: number,
  payload: {
    couponCode: string;
    paymentMethod: "pix" | "credit_card" | "debit_card";
  },
) {
  return apiRequest<DiscountCouponPreview>(
    `/api/me/event-registrations/${registrationId}/discount-coupon/validate`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload || {}),
    },
  );
}

export async function createPaymentIntentWithPayload(
  token: string,
  registrationId: number,
  payload: Partial<RegistrationPayerPayload>
) {
  return apiRequest<{
    ok: boolean;
    alreadyPaid?: boolean;
    error?: string;
    free?: boolean;
    external?: boolean;
    paymentMode?: "asaas_internal" | "external_link" | "free";
    amountCents?: number;
    baseAmountCents?: number;
    surchargeCents?: number;
    originalAmountCents?: number;
    discountAmountCents?: number;
    finalAmountCents?: number;
    couponCode?: string;
    currency?: string;
    selectedPaymentMethod?: "pix" | "credit_card" | "debit_card";
    paymentId?: number;
    paymentReference?: string;
    paymentUrl?: string | null;
    invoiceUrl?: string | null;
    status?: string;
  }>(
    `/api/me/event-registrations/${registrationId}/payment-intent`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload || {}),
    }
  );
}
